import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import sum from 'lodash-es/sum';
import { cached } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum PaymentStatus {
  authorized = 'authorized',
  pending = 'pending',
  charged = 'charged',
  markedAsPaid = 'marked_as_paid',
  failing = 'failing',
  failed = 'failed',
  refunded = 'refunded',
}

const PAID_STATUSES = [PaymentStatus.charged, PaymentStatus.markedAsPaid];

export default class PaymentModel extends Model {
  @attr('date') dueAt!: Date;
  @attr('string') currency!: string;
  @attr('number') amount!: number;
  @attr('string') status!: PaymentStatus;
  @attr('string') lastErrorMessage!: string;

  @belongsTo('booking-payment', { async: false, inverse: 'payments' })
  bookingPayment!: ModelRegistry['booking-payment'];

  @belongsTo('guest-invoice', { async: false, inverse: 'payment' })
  guestInvoice!: ModelRegistry['guest-invoice'];

  @hasMany('refund', { async: false, inverse: null })
  refunds!: ModelRegistry['refund'][];

  @cached
  get isPaid(): boolean {
    return PAID_STATUSES.includes(this.status);
  }

  @cached
  get isPending(): boolean {
    return this.status === PaymentStatus.pending;
  }

  @cached
  get isRefundable(): boolean {
    if (this.status !== PaymentStatus.charged) {
      return false;
    }

    return this.amount > this.refundSum;
  }

  @cached
  get refundSum(): number {
    return sum(this.refunds.map((item) => item.amount)) || 0;
  }

  @cached
  get maxRefundAmount(): number {
    return this.amount - this.refundSum;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    payment: PaymentModel;
  }
}
